import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import { AppConstants } from '../constants/app.constants';
import { GetNewZoneFeaturesGQL, MarkZoneFeaturesAsSeenGQL } from './release-notes.generated';

export interface ZoneFeature {
  title: string;
  content: string; // Markdown content
  iconUrl: string;
  iconBackgroundColor: string;
  featureBackgroundColor: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReleaseNotesService {
  constructor(private apollo: Apollo) {}

  /**
   * Get new features for a specific zone since the user's last app version
   * @param zone The zone identifier (e.g., "CONNECT")
   * @returns Observable of array of features
   */
  public getNewZoneFeatures(zone: string): Observable<ZoneFeature[]> {
    const query = new GetNewZoneFeaturesGQL(this.apollo);

    return query
      .fetch(
        {
          zoneCode: zone,
          appVersion: AppConstants.APP_VERSION,
        },
        {
          fetchPolicy: 'network-only',
        }
      )
      .pipe(
        map((result) => {
          const features = result.data?.auth?.getNewZoneFeaturesUsingGET?.features || [];
          // Map to ZoneFeature interface
          return features.map((f) => ({
            title: f?.title || '',
            content: f?.description || '', // description field contains markdown
            iconUrl: f?.iconUrl || '',
            iconBackgroundColor: f?.iconBackgroundColor || '',
            featureBackgroundColor: f?.featureBackgroundColor || '',
          }));
        }),
        catchError((error) => {
          console.error('Error fetching new zone features:', error);
          return of([]);
        })
      );
  }

  /**
   * Mark zone features as seen by the current user
   * @param zone The zone identifier
   * @returns Observable of success status
   */
  public markZoneFeaturesAsSeen(zone: string): Observable<boolean> {
    const mutation = new MarkZoneFeaturesAsSeenGQL(this.apollo);

    return mutation
      .mutate({
        zoneCode: zone,
        appVersion: AppConstants.APP_VERSION,
      })
      .pipe(
        map((result) => {
          // Check if mutation was successful
          return !!result.data?.auth?.markZoneFeaturesAsSeenUsingPOST;
        }),
        catchError((error) => {
          console.error('Error marking zone features as seen:', error);
          return of(false);
        })
      );
  }
}
